import {
  IonButton,
  IonButtons,
  IonCheckbox,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { close, warningOutline } from 'ionicons/icons';
import { ReactElement, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FullScreenIonModal } from '../../../../../../../components/modal/full-screen-ion-modal';
import { useMultipassPostActionCopy } from './use-multipass-post-action-copy';

export interface PassOption {
  id: string;
  label: string;
  fieldName: string;
  hasExistingActions: boolean;
}

interface MultipassPostActionCopyModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentFieldName: string;
  presentingEl?: HTMLElement;
}

export const MultipassPostActionCopyModal = (
  props: MultipassPostActionCopyModalProps
): ReactElement => {
  const { isOpen, onClose, currentFieldName, presentingEl } = props;
  const { t } = useTranslation();
  const { availablePasses, currentPassLabel, copyPostActionsToSelectedPasses } =
    useMultipassPostActionCopy(currentFieldName);
  const [selectedPassIds, setSelectedPassIds] = useState<Set<string>>(
    new Set()
  );

  const hasPassesWithActions = useMemo(
    () => availablePasses.some((pass) => pass.hasExistingActions),
    [availablePasses]
  );

  const handleTogglePass = useCallback((passId: string) => {
    setSelectedPassIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(passId)) {
        newSet.delete(passId);
      } else {
        newSet.add(passId);
      }
      return newSet;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedPassIds(new Set(availablePasses.map((pass) => pass.id)));
  }, [availablePasses]);

  const handleDeselectAll = useCallback(() => {
    setSelectedPassIds(new Set());
  }, []);

  const handleConfirm = useCallback(() => {
    const selectedPasses = availablePasses.filter((pass) =>
      selectedPassIds.has(pass.id)
    );

    copyPostActionsToSelectedPasses(selectedPasses);
    setSelectedPassIds(new Set());
    onClose();
  }, [
    availablePasses,
    selectedPassIds,
    copyPostActionsToSelectedPasses,
    onClose,
  ]);

  const handleClose = useCallback(() => {
    setSelectedPassIds(new Set());
    onClose();
  }, [onClose]);

  const selectedCount = selectedPassIds.size;

  const selectAllLabel = useMemo(() => {
    if (selectedCount === 0) {
      return t('common:select_all');
    } else if (selectedCount === availablePasses.length) {
      return t('common:unselect_all');
    } else {
      return t('common:select_all');
    }
  }, [selectedCount, availablePasses.length, t]);

  const handleSelectAllToggle = useCallback(() => {
    if (selectedCount === availablePasses.length) {
      handleDeselectAll();
    } else {
      handleSelectAll();
    }
  }, [
    selectedCount,
    availablePasses.length,
    handleSelectAll,
    handleDeselectAll,
  ]);

  return (
    <FullScreenIonModal
      isOpen={isOpen}
      onDidDismiss={handleClose}
      presentingElement={presentingEl}
    >
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton onClick={handleClose}>
              <IonIcon icon={close} size="large" />
            </IonButton>
          </IonButtons>
          <IonTitle>{t('welding:copy_post_actions')}</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <div style={{ padding: '16px 16px 8px 16px' }}>
          <p>
            {t('welding:copy_post_actions_from_to', {
              sourcePass: currentPassLabel,
            })}
          </p>
        </div>

        {hasPassesWithActions && (
          <IonItem
            color="warning"
            lines="none"
            style={{
              margin: '0 16px 16px 16px',
              '--border-radius': '8px',
            }}
          >
            <IonIcon
              icon={warningOutline}
              slot="start"
              style={{ fontSize: '24px' }}
            />
            <IonLabel>{t('welding:copy_post_actions_warning')}</IonLabel>
          </IonItem>
        )}

        {availablePasses.length > 1 && (
          <IonItem button onClick={handleSelectAllToggle}>
            <IonCheckbox
              slot="start"
              checked={selectedCount === availablePasses.length}
              indeterminate={
                selectedCount > 0 && selectedCount < availablePasses.length
              }
            />
            <IonLabel>
              <strong>{selectAllLabel}</strong>
            </IonLabel>
          </IonItem>
        )}

        <IonList>
          {availablePasses.map((pass) => (
            <IonItem
              key={pass.id}
              button
              onClick={() => handleTogglePass(pass.id)}
            >
              <IonCheckbox
                slot="start"
                checked={selectedPassIds.has(pass.id)}
              />
              <IonLabel>{pass.label}</IonLabel>
              {pass.hasExistingActions && (
                <IonIcon
                  icon={warningOutline}
                  slot="end"
                  style={{
                    color: '#ffd65d',
                    fontSize: '20px',
                  }}
                />
              )}
            </IonItem>
          ))}
        </IonList>
      </IonContent>

      <IonFooter>
        <IonToolbar>
          <IonButtons slot="end">
            <IonButton fill="clear" onClick={handleClose}>
              {t('common:cancel')}
            </IonButton>
            <IonButton
              fill="solid"
              color="primary"
              onClick={handleConfirm}
              disabled={selectedCount === 0}
            >
              {t('common:copy_to')} ({selectedCount})
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonFooter>
    </FullScreenIonModal>
  );
};
